import { memo, useState, useEffect, useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import { useBuilderTranslation } from '@/i18n/hooks/useTypedTranslation';
import type { QuestionOption } from '@/types';

interface OptionItemProps {
  option: QuestionOption;
  index: number;
  onChange: (id: number, label: string) => void;
  onDelete: (id: number) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

export const OptionItem = memo(function OptionItem({
  option,
  index,
  onChange,
  onDelete,
  disabled = false,
  autoFocus = false,
}: OptionItemProps) {
  const { t } = useBuilderTranslation();
  // Local state for text input to prevent parent re-renders on every keystroke
  const [localLabel, setLocalLabel] = useState(option.label);

  // Sync local state when option.label changes from parent (e.g., undo/redo)
  // This is intentional - syncing local state with prop for controlled input optimization
  useEffect(() => {
    setLocalLabel(option.label);
  }, [option.label]);

  // Only sync to parent on blur to prevent expensive re-renders
  const handleBlur = useCallback(() => {
    if (localLabel !== option.label) {
      onChange(option.id, localLabel);
    }
  }, [localLabel, option.label, option.id, onChange]);

  const handleDelete = useCallback(() => {
    onDelete(option.id);
  }, [onDelete, option.id]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        maxWidth: '100%',
        ...(isDragging && { zIndex: 50 }),
      }}
    >
      <IconButton
        {...attributes}
        {...listeners}
        size="small"
        disabled={disabled}
        sx={{
          cursor: disabled ? 'default' : 'grab',
          color: 'text.secondary',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ width: 24, flexShrink: 0 }}
      >
        {String.fromCharCode(65 + index)}.
      </Typography>

      <TextField
        size="small"
        fullWidth
        value={localLabel}
        onChange={(e) => setLocalLabel(e.target.value)}
        onBlur={handleBlur}
        placeholder={t('options.placeholder', { number: index + 1 })}
        disabled={disabled}
        autoFocus={autoFocus}
        sx={{ minWidth: 0, flex: 1 }}
      />

      <IconButton
        size="small"
        onClick={handleDelete}
        disabled={disabled}
        sx={{
          color: 'text.secondary',
          '&:hover': { color: 'error.main' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

export default OptionItem;
